import type { ClubRole, PlatformRole } from "@rivaled/shared";
import {
  type ClubPermissionInput,
  clubPermissionStatements,
  clubRoleAdditions,
  mergePermissionStatements,
  type PlatformPermissionInput,
  platformPermissionStatements,
  platformRoleAdditions,
  statementMapHasPermission,
} from "@rivaled/shared";
import { createAccessControl } from "better-auth/plugins/access";
import {
  defaultRoles as defaultAdminRoles,
  defaultStatements as defaultAdminStatements,
} from "better-auth/plugins/admin/access";
import {
  defaultRoles as defaultOrganizationRoles,
  defaultStatements as defaultOrganizationStatements,
} from "better-auth/plugins/organization/access";

export const platformAc = createAccessControl({
  ...defaultAdminStatements,
  ...platformPermissionStatements,
});
export const clubAc = createAccessControl({
  ...defaultOrganizationStatements,
  ...clubPermissionStatements,
});

export const platformRoles = {
  admin: platformAc.newRole(
    mergePermissionStatements(
      defaultAdminRoles.admin.statements,
      platformRoleAdditions.admin,
    ) as never,
  ),
  developer: platformAc.newRole(
    mergePermissionStatements(platformRoleAdditions.developer) as never,
  ),
  moderator: platformAc.newRole(
    mergePermissionStatements(platformRoleAdditions.moderator) as never,
  ),
} as const;

export const platformPluginRoles = {
  ...defaultAdminRoles,
  ...platformRoles,
} as const;

export const clubRoles = {
  admin: clubAc.newRole(
    mergePermissionStatements(
      defaultOrganizationRoles.admin.statements,
      clubRoleAdditions.admin,
    ) as never,
  ),
  member: clubAc.newRole(
    mergePermissionStatements(
      defaultOrganizationRoles.member.statements,
      clubRoleAdditions.member,
    ) as never,
  ),
  moderator: clubAc.newRole(
    mergePermissionStatements(
      defaultOrganizationRoles.member.statements,
      clubRoleAdditions.moderator,
    ) as never,
  ),
  owner: clubAc.newRole(
    mergePermissionStatements(
      defaultOrganizationRoles.owner.statements,
      clubRoleAdditions.owner,
    ) as never,
  ),
} as const;

export const clubPluginRoles = {
  ...defaultOrganizationRoles,
  ...clubRoles,
} as const;

export function canClubRolePermission(
  role: ClubRole | null | undefined,
  permission: ClubPermissionInput,
): boolean {
  if (!role) {
    return false;
  }

  return statementMapHasPermission(clubRoles[role].statements, permission);
}

export function canPlatformRolePermission(
  role: PlatformRole | null | undefined,
  permission: PlatformPermissionInput,
): boolean {
  if (!role) {
    return false;
  }

  return statementMapHasPermission(platformRoles[role].statements, permission);
}

export function hasAnyClubPermission(
  role: ClubRole | null | undefined,
  permissions: readonly ClubPermissionInput[],
): boolean {
  return permissions.some((permission) => canClubRolePermission(role, permission));
}
